/**
 * Shared single-campaign frame for Discover and My Campaigns.
 * The cover stays 3:4. Its height follows the column beside it, and its width
 * follows that height so the photo keeps its ratio.
 */
import type { ReactNode } from "react";
import { Badge, Card, Eyebrow } from "@ui/primitives";
import { FieldTip } from "@ui/forms";
import { fontSize } from "@design/index";
import { CampaignCover } from "@app/shared/CampaignTile";
import { PlatformIcon, visiblePlatforms } from "@app/shared/Platforms";
import { CardListSkeleton, TileRowSkeleton } from "@app/shared/Skeletons";

export interface ShowcaseStat {
  readonly label: string;
  readonly value: ReactNode;
  readonly hint?: string;
}

export function CampaignDetailSkeleton() {
  return (
    <div role="status" aria-label="Loading campaign" style={{ display: "grid", gap: 24 }}>
      <div className="cm-campaign-hero" style={{
        display: "grid", gap: 28, gridTemplateColumns: "minmax(280px, auto) minmax(0, 1fr)",
        alignItems: "stretch",
      }}>
        <div className="cm-skel cm-campaign-hero-cover" style={{ borderRadius: 22, width: "100%", height: "100%" }} />
        <div style={{ display: "grid", gap: 16, alignContent: "start" }}>
          <TileRowSkeleton count={2} />
          <CardListSkeleton count={1} />
        </div>
      </div>
    </div>
  );
}

export function CampaignShowcase({
  backHref, backLabel, onBack, brand, brandTrailing, name, summary, badge, stats,
  afterStats, actions, afterSummary, children, aside, coverUrl, showBack = true,
}: {
  backHref: string;
  backLabel: string;
  onBack: () => void;
  brand: string;
  brandTrailing?: ReactNode;
  name: string;
  summary?: string | null;
  badge?: string | null;
  stats: readonly ShowcaseStat[];
  afterStats?: ReactNode;
  actions?: ReactNode;
  afterSummary?: ReactNode;
  children?: ReactNode;
  aside?: ReactNode;
  coverUrl?: string | null;
  showBack?: boolean;
}) {
  return (
    <div style={{ display: "grid", gap: 24 }}>
      {showBack ? (
      <a
        href={backHref}
        onClick={(e) => { e.preventDefault(); onBack(); }}
        style={{
          fontSize: fontSize.bodyLg, color: "var(--link)", textDecoration: "none",
          fontWeight: 600, display: "inline-block",
        }}
      >
        ← {backLabel}
      </a>
      ) : null}

      <div
        className="cm-campaign-hero"
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 28,
        }}
      >
        <figure className="cm-campaign-hero-cover" style={{
          position: "relative", margin: 0,
          borderRadius: 22, overflow: "hidden", border: "1px solid var(--line)",
          background: "var(--surface)",
        }}>
          {coverUrl ? (
            <img data-tile-media="" src={coverUrl} alt=""
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <CampaignCover brand={brand} name={name} />
          )}
          {badge ? (
            <figcaption style={{
              position: "absolute", top: 14, left: 14, padding: "5px 11px",
              borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: ".04em",
              background: "var(--accent)", color: "var(--accent-fg)",
            }}>
              {badge}
            </figcaption>
          ) : null}
        </figure>

        <div className="cm-campaign-hero-copy" style={{ display: "grid", gap: 16, alignContent: "start", minWidth: 0, paddingTop: 4, flex: "1 1 auto" }}>
          <p style={{
            margin: 0, display: "flex", alignItems: "center", gap: 8,
            fontSize: 13, fontWeight: 650, color: "var(--muted)", letterSpacing: "0.02em",
          }}>
            {brand}
            {brandTrailing}
          </p>
          <div className="cm-campaign-title-row">
            <h1 className="cm-campaign-title" style={{
              margin: 0, fontSize: "clamp(28px, 3.4vw, 42px)", fontWeight: 800,
              letterSpacing: "-0.035em", lineHeight: 1.08, minWidth: 0,
            }}>
              {name}
            </h1>
            {actions ? <div className="cm-campaign-title-actions">{actions}</div> : null}
          </div>
          {summary ? (
            <p style={{ margin: 0, fontSize: 16, color: "var(--text-2)", lineHeight: 1.6, maxWidth: 640 }}>
              {summary}
            </p>
          ) : null}
          {afterSummary ? (
            <div className="cm-campaign-after-summary" style={{
              display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
            }}>
              {afterSummary}
            </div>
          ) : null}

          <div
            className="cm-work-stats cm-campaign-stats"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 10,
              marginTop: 4,
            }}
          >
            {stats.map((s) => (
              <div key={s.label} className="cm-campaign-stat" style={{
                background: "var(--surface)", border: "1px solid var(--line)",
                borderRadius: 16, padding: "14px 14px 12px", display: "grid", gap: 4,
                minWidth: 0, alignContent: "start",
              }}>
                <span style={{ fontSize: fontSize.label, color: "var(--muted)", fontWeight: 600 }}>
                  {s.label}
                </span>
                <strong style={{
                  fontWeight: 750, letterSpacing: "-0.03em",
                  fontVariantNumeric: "tabular-nums", lineHeight: 1.2,
                }}>
                  {s.value}
                </strong>
                {s.hint ? (
                  <span style={{ fontSize: fontSize.micro, color: "var(--faint)", lineHeight: 1.35 }}>{s.hint}</span>
                ) : null}
              </div>
            ))}
          </div>
          {afterStats ? <div>{afterStats}</div> : null}
        </div>
      </div>

      {children || aside ? (
        <div
          className="cm-work-detail"
          style={{
            display: "grid",
            gap: 20,
            gridTemplateColumns: "minmax(0, 1.4fr) minmax(280px, .8fr)",
            alignItems: "start",
          }}
        >
          {children}
          <aside style={{ display: "grid", gap: 16 }}>{aside}</aside>
        </div>
      ) : null}
    </div>
  );
}

export type CampaignSectionId = "general" | "brief" | "submissions";

export function CampaignSectionTabs({
  value, onChange, includeSubmissions = false, submissionCount = 0,
}: {
  value: CampaignSectionId;
  onChange: (id: CampaignSectionId) => void;
  includeSubmissions?: boolean;
  submissionCount?: number;
}) {
  const tabs: { id: CampaignSectionId; label: string; count?: number }[] = [
    { id: "general", label: "General" },
    { id: "brief", label: "Brief" },
    ...(includeSubmissions
      ? [{ id: "submissions" as const, label: "Submissions", count: submissionCount }]
      : []),
  ];
  return (
    <div className="cm-dossier-tabs" role="tablist" aria-label="Campaign sections">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          id={`campaign-tab-${t.id}`}
          aria-selected={value === t.id}
          aria-controls={`campaign-panel-${t.id}`}
          onClick={() => onChange(t.id)}
          className="cm-dossier-tab"
        >
          {t.label}
          {t.count != null ? <span className="cm-dossier-tab-count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

function SectionHeading({ title, info }: { title: string; info?: string }) {
  return (
    <h2 className="cm-campaign-section-title">
      {title}
      {info ? <FieldTip text={info} /> : null}
    </h2>
  );
}

export function ScheduleBoard({
  rows, note, info,
}: {
  rows: readonly { label: string; value: string }[];
  note?: string;
  info?: string;
}) {
  return (
    <section className="cm-campaign-section">
      <SectionHeading title="Schedule" {...(info ? { info } : {})} />
      <div className="cm-schedule-board">
        {rows.map((row) => (
          <article key={row.label} className="cm-schedule-tile">
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </article>
        ))}
      </div>
      {note ? <p className="cm-section-note">{note}</p> : null}
    </section>
  );
}

export function PlatformBoard({
  routes, facts, note, info, children,
}: {
  routes: readonly string[];
  facts?: readonly { id: string; status: string; tone: "ok" | "warn" | "neutral" | "danger" }[];
  note?: string;
  info?: string;
  children?: ReactNode;
}) {
  const listed = visiblePlatforms(routes);
  const byId = new Map((facts ?? []).map((fact) => [fact.id, fact]));
  if (listed.length === 0 && !children) return null;
  return (
    <section className="cm-campaign-section">
      <SectionHeading title="Required platforms" {...(info ? { info } : {})} />
      {listed.length > 0 ? (
        <div className="cm-platform-board">
          {listed.map((platform) => {
            const fact = byId.get(platform.id);
            return (
              <article key={platform.id} className="cm-platform-card">
                <PlatformIcon id={platform.id} size={28} title={platform.label} />
                <strong>{platform.label}</strong>
                {fact ? <Badge tone={fact.tone}>{fact.status}</Badge> : null}
              </article>
            );
          })}
        </div>
      ) : (
        <p className="cm-section-note">Not listed</p>
      )}
      {note ? <p className="cm-section-note">{note}</p> : null}
      {children}
    </section>
  );
}

export function AssetBoard({
  assets,
}: {
  assets: readonly { slot: string; filename: string }[];
}) {
  if (assets.length === 0) return null;
  return (
    <section className="cm-campaign-section">
      <SectionHeading title="Brand assets" />
      <div className="cm-asset-board">
        {assets.map((asset) => {
          const ext = asset.filename.includes(".")
            ? asset.filename.split(".").pop()!.slice(0, 4).toUpperCase()
            : "FILE";
          const slot = asset.slot.replaceAll("_", " ");
          return (
            <article key={`${asset.slot}-${asset.filename}`} className="cm-asset-card">
              <span className="cm-asset-mark" aria-hidden="true">{ext || "FILE"}</span>
              <div className="cm-asset-copy">
                <strong title={asset.filename}>{asset.filename}</strong>
                <span>{slot}</span>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function BriefColumn({
  brief, emptyAction,
}: {
  brief: Record<string, unknown>;
  emptyAction?: ReactNode;
}) {
  const sections: [string, string][] = [
    ["deliverables", "What to post"],
    ["creative_directions", "Creative direction"],
    ["hashtags", "Hashtags and mentions"],
    ["posting_requirements", "Posting rules"],
    ["prohibited", "Do not include"],
    ["usage_rights", "Usage rights"],
  ];
  const present = sections.filter(([k]) => typeof brief[k] === "string" && (brief[k] as string).trim());

  return (
    <Card padding={26} style={{ display: "grid", gap: 20, borderRadius: 20 }}>
      <div style={{ display: "grid", gap: 6 }}>
        <Eyebrow>The brief</Eyebrow>
        <h2 style={{ margin: 0, fontSize: fontSize.h3, fontWeight: 700, letterSpacing: "-0.02em" }}>
          What the brand wants
        </h2>
      </div>
      {present.length === 0 ? (
        <div style={{ display: "grid", gap: 14 }}>
          <p style={{ margin: 0, fontSize: fontSize.bodyLg, color: "var(--muted)", lineHeight: 1.65 }}>
            The brand has not published the brief yet. The rules will appear here
            before you need to film or post.
          </p>
          {emptyAction}
        </div>
      ) : present.map(([k, label], i) => (
        <section key={k} style={{
          display: "grid", gap: 8,
          borderTop: i === 0 ? "none" : "1px solid var(--line)",
          paddingTop: i === 0 ? 0 : 16,
        }}>
          <h3 style={{ margin: 0, fontSize: fontSize.bodyLg, fontWeight: 700 }}>{label}</h3>
          <p style={{
            margin: 0, fontSize: fontSize.bodyLg, color: "var(--text-2)",
            lineHeight: 1.7, whiteSpace: "pre-wrap",
          }}>
            {brief[k] as string}
          </p>
        </section>
      ))}
    </Card>
  );
}

export function SidePanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card padding={20} style={{ display: "grid", gap: 14, borderRadius: 18 }}>
      <Eyebrow>{title}</Eyebrow>
      {children}
    </Card>
  );
}

export function ScheduleList({
  rows, note,
}: {
  rows: readonly { label: string; value: string }[];
  note?: string;
}) {
  return (
    <>
      <div style={{ display: "grid", gap: 0 }}>
        {rows.map((row, i) => (
          <div key={row.label} style={{
            display: "flex", justifyContent: "space-between", gap: 16, alignItems: "baseline",
            padding: "10px 0",
            borderTop: i === 0 ? "none" : "1px solid var(--line)",
          }}>
            <span style={{ fontSize: fontSize.body, color: "var(--muted)" }}>{row.label}</span>
            <strong style={{ fontSize: fontSize.bodyLg, fontWeight: 650, textAlign: "right" }}>
              {row.value}
            </strong>
          </div>
        ))}
      </div>
      {note ? (
        <p style={{ margin: 0, fontSize: fontSize.micro, color: "var(--faint)", lineHeight: 1.6 }}>
          {note}
        </p>
      ) : null}
    </>
  );
}
